import * as React from "react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "motion/react"
import confetti from "canvas-confetti"

import { useAuth } from "@/contexts/AuthContext"
import ovalMoneyClubLogo from "@/public/oval-money-club-logo.png"
import ovalLogo from "@/public/oval-logo.svg"
import coinImg from "@/public/coin.png"
import moneyImg from "@/public/money.png"
import ovalPillImg from "@/public/oval-pill.png"
import { Star, Sparkles, DollarSign, ArrowRight } from "lucide-react"

type WelcomeSpinPrize = {
  symbol: string
  amount: number
  probabilityPct: number
  label: string
}

const DEFAULT_WELCOME_SPIN_PRIZES: WelcomeSpinPrize[] = [
  { symbol: "$100", amount: 100, probabilityPct: 2, label: "JACKPOT!" },
  { symbol: "$50", amount: 50, probabilityPct: 5, label: "BIG WIN!" },
  { symbol: "$20", amount: 20, probabilityPct: 10, label: "GREAT!" },
  { symbol: "$10", amount: 10, probabilityPct: 15, label: "NICE!" },
  { symbol: "$5", amount: 5, probabilityPct: 20, label: "GOOD!" },
  { symbol: "$2", amount: 2, probabilityPct: 23, label: "WIN!" },
  { symbol: "$1", amount: 1, probabilityPct: 25, label: "WIN!" },
]

function normalizeWelcomeSpinPrizes(
  raw: Array<{ amount: number; label: string; probabilityPct: number }> | undefined
): WelcomeSpinPrize[] {
  if (!Array.isArray(raw)) return DEFAULT_WELCOME_SPIN_PRIZES

  const normalized = raw
    .filter((row) => Number.isFinite(row?.amount) && Number.isFinite(row?.probabilityPct))
    .map((row) => ({
      amount: Math.max(0, Number(row.amount)),
      probabilityPct: Math.max(0, Number(row.probabilityPct)),
      label: typeof row.label === "string" && row.label.trim() ? row.label.trim() : "WIN!",
      symbol: `$${Math.max(0, Number(row.amount))}`,
    }))
    .filter((row) => row.probabilityPct > 0)

  return normalized.length > 0 ? normalized : DEFAULT_WELCOME_SPIN_PRIZES
}

// --- Spinning reel with retro arcade styling ---
function SpinningReel({
  isSpinning,
  finalSymbol,
  symbols,
  delay,
  index,
}: {
  isSpinning: boolean
  finalSymbol: string
  symbols: string[]
  delay: number
  index: number
}) {
  const [displaySymbol, setDisplaySymbol] = useState<string>("$1")
  const [isReelSpinning, setIsReelSpinning] = useState(false)
  const [stopped, setStopped] = useState(false)
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (isSpinning) {
      setIsReelSpinning(true)
      setStopped(false)
      intervalRef.current = setInterval(() => {
        setDisplaySymbol(symbols[Math.floor(Math.random() * symbols.length)] ?? "$1")
      }, 55)
      const stopTimeout = setTimeout(() => {
        if (intervalRef.current) clearInterval(intervalRef.current)
        setDisplaySymbol(finalSymbol)
        setIsReelSpinning(false)
        setStopped(true)
      }, 1200 + delay)
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current)
        clearTimeout(stopTimeout)
      }
    }
  }, [isSpinning, finalSymbol, delay, symbols])

  const getBillStyle = (symbol: string) => {
    switch (symbol) {
      case "$100": return { bg: "from-[#FFD700] to-[#FFA500]", text: "text-black", border: "border-[#B8860B]" }
      case "$50": return { bg: "from-[#C4A052] to-[#D4B062]", text: "text-white", border: "border-[#c45a1a]" }
      case "$20": return { bg: "from-[#32CD32] to-[#228B22]", text: "text-white", border: "border-[#1a6b1a]" }
      case "$10": return { bg: "from-[#9B59B6] to-[#7B68EE]", text: "text-white", border: "border-[#6a3d80]" }
      case "$5": return { bg: "from-[#00CED1] to-[#008B8B]", text: "text-white", border: "border-[#006666]" }
      case "$2": return { bg: "from-[#4169E1] to-[#2E4FBA]", text: "text-white", border: "border-[#1a3080]" }
      default: return { bg: "from-[#90EE90] to-[#32CD32]", text: "text-black", border: "border-[#228B22]" }
    }
  }

  const style = getBillStyle(displaySymbol)

  return (
    <motion.div
      className="relative flex-1 overflow-hidden rounded-2xl"
      style={{
        background: "#121220",
        border: "1px solid #2A2A45",
        boxShadow: "inset 0 2px 10px rgba(0,0,0,0.4)",
        minHeight: "110px",
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 + index * 0.1, type: "spring", bounce: 0.3 }}
    >
      {/* Glass highlight */}
      <div
        className="absolute inset-0 pointer-events-none z-10 rounded-2xl"
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 40%, transparent 80%, rgba(0,0,0,0.2) 100%)",
        }}
      />

      <motion.div
        className="w-full h-full flex items-center justify-center p-3"
        animate={isReelSpinning ? { y: [0, -8, 0] } : { y: 0 }}
        transition={isReelSpinning ? { duration: 0.08, repeat: Infinity } : {}}
      >
        <motion.div
          className={`
            w-full py-4 rounded-xl bg-gradient-to-b ${style.bg}
            flex items-center justify-center
            border-3 ${style.border}
            shadow-[2px_2px_0_0_rgba(0,0,0,0.3)]
          `}
          style={{
            filter: isReelSpinning ? "blur(3px) brightness(1.2)" : "none",
          }}
          animate={
            stopped
              ? { scale: [1, 1.15, 1], rotate: [0, -3, 3, 0] }
              : {}
          }
          transition={{ duration: 0.4, type: "spring" }}
        >
          <span
            className={`text-3xl font-black ${style.text} drop-shadow-md`}
            style={{
              fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
              letterSpacing: "0.05em",
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            {displaySymbol}
          </span>
        </motion.div>
      </motion.div>
    </motion.div>
  )
}

// --- Slot Machine ---
function BankNoteSlotMachine({
  onWin,
  onSpinServer,
  prizes,
}: {
  onWin: (amount: number) => void
  onSpinServer: () => Promise<{ success: boolean; message?: string; reward?: { amount: number; label: string } }>
  prizes: WelcomeSpinPrize[]
}) {
  const [gameState, setGameState] = useState<"ready" | "spinning" | "won">("ready")
  const [result, setResult] = useState<{ symbol: string; amount: number; label: string } | null>(null)
  const [spinError, setSpinError] = useState<string | null>(null)
  const reelSymbols = React.useMemo(() => {
    const unique = Array.from(new Set(prizes.map((prize) => prize.symbol).filter(Boolean)))
    return unique.length > 0 ? unique : DEFAULT_WELCOME_SPIN_PRIZES.map((prize) => prize.symbol)
  }, [prizes])
  const prizeRangeLabel = React.useMemo(() => {
    const amounts = prizes.map((prize) => prize.amount).filter((value) => Number.isFinite(value))
    if (amounts.length === 0) return "Win Cash!"
    const min = Math.min(...amounts)
    const max = Math.max(...amounts)
    return `Win $${min} – $${max} Cash!`
  }, [prizes])

  const spin = async () => {
    if (gameState !== "ready") return
    setSpinError(null)
    setGameState("spinning")
    const serverResult = await onSpinServer()
    if (!serverResult.success || !serverResult.reward) {
      setGameState("ready")
      setSpinError(serverResult.message || "Spin failed. Please try again.")
      return
    }

    const matchedPrize =
      prizes.find((prize) => prize.amount === serverResult.reward?.amount) ??
      DEFAULT_WELCOME_SPIN_PRIZES.find((prize) => prize.amount === serverResult.reward?.amount)
    const winResult = {
      symbol: matchedPrize?.symbol || `$${serverResult.reward.amount}`,
      amount: serverResult.reward.amount,
      label: serverResult.reward.label || matchedPrize?.label || "WIN!",
    }
    setResult(winResult)

    setTimeout(() => {
      setGameState("won")
      const colors = ["#F37020", "#FFD700", "#32CD32", "#9B59B6", "#FF69B4", "#00CED1"]
      confetti({ particleCount: 180, spread: 140, origin: { y: 0.5, x: 0.5 }, colors })
      setTimeout(() => {
        confetti({ particleCount: 80, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors })
        confetti({ particleCount: 80, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors })
      }, 250)
      onWin(winResult.amount)
    }, 2800)
  }

  return (
    <div className="space-y-4">
      {/* Slot machine cabinet */}
      <motion.div
        className="relative rounded-3xl overflow-hidden"
        style={{
          background: "#121220",
          border: "1px solid #2A2A45",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
      >
        {/* Gold trim border */}
        <div
          className="absolute inset-[3px] rounded-[20px] pointer-events-none z-20"
          style={{
            border: "none",
          }}
        />

        {/* Top light strip */}
        <div className="relative flex items-center justify-center gap-2 py-3 px-4"
          style={{ background: "#1A1A30" }}
        >
          {[...Array(9)].map((_, i) => (
            <motion.div
              key={i}
              className="w-2.5 h-2.5 rounded-full"
              style={{
                background: i % 3 === 0 ? "#F37020" : i % 3 === 1 ? "#FFD700" : "#32CD32",
                boxShadow: `0 0 6px ${i % 3 === 0 ? "#F37020" : i % 3 === 1 ? "#FFD700" : "#32CD32"}`,
              }}
              animate={{ opacity: [0.3, 1, 0.3], scale: [0.85, 1.1, 0.85] }}
              transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.08 }}
            />
          ))}
        </div>

        {/* Title area */}
        <motion.div
          className="text-center py-3 px-4"
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h3
            className="text-2xl font-black tracking-wider"
            style={{
              fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
              letterSpacing: "0.1em",
              background: "linear-gradient(180deg, #FFD700 0%, #C4A052 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "none",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
            }}
          >
            WELCOME SPIN
          </h3>
          <p
            className="text-[#9A9ABF] text-sm font-medium mt-0.5"
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            {prizeRangeLabel}
          </p>
        </motion.div>

        {/* Reel window */}
        <div className="px-4 pb-4">
          <div
            className="flex gap-2.5 p-3 rounded-2xl"
            style={{
              background: "#121220",
              border: "1px solid #2A2A45",
              boxShadow: "inset 0 2px 8px rgba(0,0,0,0.3)",
            }}
          >
            <SpinningReel isSpinning={gameState === "spinning"} finalSymbol={result?.symbol || "$1"} symbols={reelSymbols} delay={0} index={0} />
            <SpinningReel isSpinning={gameState === "spinning"} finalSymbol={result?.symbol || "$5"} symbols={reelSymbols} delay={400} index={1} />
            <SpinningReel isSpinning={gameState === "spinning"} finalSymbol={result?.symbol || "$10"} symbols={reelSymbols} delay={800} index={2} />
          </div>
        </div>

        {/* Spin button area */}
        <div className="px-4 pb-5">
          {gameState === "ready" && (
            <motion.button
              onClick={spin}
              className="w-full py-4 rounded-2xl font-black text-xl tracking-wider text-white cursor-pointer relative overflow-hidden"
              style={{
                fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
                letterSpacing: "0.08em",
                background: "linear-gradient(135deg, #F37020 0%, #FF8C42 100%)",
                border: "1px solid #2A2A45",
                boxShadow: "0 4px 12px rgba(243, 112, 32, 0.3)",
              }}
              whileHover={{ scale: 1.03, y: -2, boxShadow: "0 7px 0 #1a1a1a, 6px 6px 0 0 #1a1a1a, 0 12px 40px rgba(243, 112, 32, 0.5)" }}
              whileTap={{ scale: 0.98, y: 4, boxShadow: "0 1px 0 #1a1a1a, 1px 1px 0 0 #1a1a1a" }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {/* Shimmer */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", repeatDelay: 1 }}
              />
              <span className="relative">SPIN TO WIN!</span>
            </motion.button>
          )}

          {gameState === "spinning" && (
            <motion.div
              className="w-full py-4 rounded-2xl font-black text-xl tracking-wider text-center text-[#9A9ABF]"
              style={{
                fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
                letterSpacing: "0.08em",
                background: "#1A1A30",
                border: "1px solid #2A2A45",
                boxShadow: "0 3px 0 #1a1a1a",
              }}
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 0.5, repeat: Infinity }}
            >
              SPINNING...
            </motion.div>
          )}
        </div>

        {/* Bottom light strip */}
        <div className="flex items-center justify-center gap-2 py-2 px-4"
          style={{ background: "#1A1A30" }}
        >
          {[...Array(9)].map((_, i) => (
            <motion.div
              key={i}
              className="w-2 h-2 rounded-full"
              style={{
                background: i % 3 === 0 ? "#F37020" : i % 3 === 1 ? "#FFD700" : "#32CD32",
                boxShadow: `0 0 4px ${i % 3 === 0 ? "#F37020" : i % 3 === 1 ? "#FFD700" : "#32CD32"}`,
              }}
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 0.8, repeat: Infinity, delay: (8 - i) * 0.08 }}
            />
          ))}
        </div>
      </motion.div>

      {spinError && (
        <div className="text-center text-xs text-red-300 font-semibold bg-red-400/10 border border-red-300/20 rounded-xl px-3 py-2">
          {spinError}
        </div>
      )}

      {/* Prize ladder */}
      <motion.div
        className="grid grid-cols-4 gap-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        {prizes.slice(0, 4).map((prize, i) => (
          <motion.div
            key={i}
            className="flex flex-col items-center py-2.5 px-2 rounded-xl bg-[#1A1A30] border border-[#2A2A45] shadow-sm"
            whileHover={{ y: -2, boxShadow: "3px 3px 0 0 #1a1a1a" }}
          >
            <span
              className="font-black text-sm text-white"
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif", letterSpacing: "0.05em" }}
            >
              {prize.symbol}
            </span>
            <span
              className="text-[10px] font-bold text-[#32CD32]"
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              {prize.label}
            </span>
          </motion.div>
        ))}
      </motion.div>
    </div>
  )
}

// --- Win celebration ---
function WinCelebration({ amount, onContinue }: { amount: number; onContinue: () => void }) {
  const [showMessage, setShowMessage] = useState(false)
  useEffect(() => {
    setTimeout(() => setShowMessage(true), 300)
  }, [])

  const isJackpot = amount >= 50
  const isBigWin = amount >= 20

  return (
    <motion.div
      className="text-center space-y-5 py-2"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 200 }}
    >
      {/* Mascot celebration */}
      <motion.div
        className="relative w-24 h-24 mx-auto"
        animate={{
          rotate: [-5, 5, -5],
          y: [0, -10, 0],
        }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <img src={ovalPillImg} alt="" className="w-full h-full drop-shadow-lg" />
        {/* Sparkle particles around mascot */}
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              top: `${[10, 0, 50, 70][i]}%`,
              left: `${[-15, 90, 105, -10][i]}%`,
            }}
            animate={{
              scale: [0, 1.2, 0],
              opacity: [0, 1, 0],
              rotate: [0, 180],
            }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
              delay: i * 0.3,
            }}
          >
            <Sparkles className="w-4 h-4 text-[#FFD700]" />
          </motion.div>
        ))}
      </motion.div>

      <AnimatePresence>
        {showMessage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Win label */}
            <h2
              className="text-4xl sm:text-5xl font-black"
              style={{
                fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
                letterSpacing: "0.06em",
                background: isJackpot
                  ? "linear-gradient(180deg, #FFD700 0%, #D4B062 100%)"
                  : isBigWin
                  ? "linear-gradient(180deg, #FFD700 0%, #FFA500 100%)"
                  : "linear-gradient(180deg, #32CD32 0%, #228B22 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.3))",
              }}
            >
              {isJackpot ? "JACKPOT!" : isBigWin ? "BIG WIN!" : "YOU WON!"}
            </h2>

            {/* Amount display */}
            <motion.div
              className="relative inline-block"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300, bounce: 0.5 }}
            >
              <motion.div
                className="relative bg-[#1C1C32] border border-[#2A2A45] rounded-xl shadow-lg px-8 py-4 inline-block"
                animate={{ boxShadow: ["6px 6px 0 0 #1a1a1a", "8px 8px 0 0 #1a1a1a", "6px 6px 0 0 #1a1a1a"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                {/* Floating coins around the amount */}
                <motion.img
                  src={coinImg}
                  alt=""
                  className="absolute -top-4 -left-4 w-10 h-10"
                  animate={{ rotate: [0, 360], y: [0, -5, 0] }}
                  transition={{
                    rotate: { duration: 3, repeat: Infinity, ease: "linear" },
                    y: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
                  }}
                />
                <motion.img
                  src={moneyImg}
                  alt=""
                  className="absolute -bottom-3 -right-5 w-12 h-auto"
                  animate={{ rotate: [-5, 5, -5], y: [0, -3, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />

                <motion.span
                  className="text-5xl sm:text-6xl font-black text-[#32CD32]"
                  style={{
                    fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
                    letterSpacing: "0.04em",
                    textShadow: "3px 3px 0 #1a6b1a",
                  }}
                  animate={{ scale: [1, 1.04, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  ${amount}
                </motion.span>
              </motion.div>
            </motion.div>

            {/* Claim warning */}
            <motion.div
              className="bg-[#222240] border border-[#F37020] rounded-xl p-4 mx-auto max-w-sm shadow-[3px_3px_0_0_rgba(180,140,0,0.3)]"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center gap-2 justify-center mb-1">
                <DollarSign className="w-4 h-4 text-[#32CD32]" />
                <p
                  className="text-white font-bold text-sm"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  Your <span className="text-[#32CD32] font-black">${amount}</span> is pending!
                </p>
              </div>
              <p
                className="text-[#F37020] font-medium text-xs"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
              >
                Submit a deal within 24 hours to claim!
              </p>
            </motion.div>

            {/* Continue button */}
            <motion.div
              className="pt-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <motion.button
                onClick={onContinue}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-black text-lg text-white cursor-pointer relative overflow-hidden"
                style={{
                  fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
                  background: "linear-gradient(135deg, #F37020 0%, #FF8C42 100%)",
                  border: "1px solid #2A2A45",
                  boxShadow: "0 4px 12px rgba(243, 112, 32, 0.3)",
                }}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97, y: 4, boxShadow: "0 1px 0 #1a1a1a, 1px 1px 0 0 #1a1a1a" }}
              >
                {/* Shimmer */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  animate={{ x: ["-100%", "200%"] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.5 }}
                />
                <span className="relative">Continue to Dashboard</span>
                <motion.span
                  className="relative"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.span>
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// --- Main page ---
export function WelcomeSpinPage() {
  const { getWelcomeSpinConfig, spinWelcomeReward, refreshUser } = useAuth()
  const navigate = useNavigate()
  const [winAmount, setWinAmount] = useState<number | null>(null)
  const [isContinuing, setIsContinuing] = useState(false)
  const [prizes, setPrizes] = useState<WelcomeSpinPrize[]>(DEFAULT_WELCOME_SPIN_PRIZES)

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      const result = await getWelcomeSpinConfig()
      if (!cancelled && result.success) {
        setPrizes(normalizeWelcomeSpinPrizes(result.prizes))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [getWelcomeSpinConfig])

  const handleWin = (amount: number) => {
    setWinAmount(amount)
  }

  const handleServerSpin = async () => {
    return await spinWelcomeReward()
  }

  const handleContinue = async () => {
    if (winAmount === null || isContinuing) return
    setIsContinuing(true)
    try {
      await refreshUser()
      navigate("/dashboard")
    } catch (error) {
      console.error("Failed to refresh user after welcome spin:", error)
      setIsContinuing(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 overflow-hidden relative">
      <div className="absolute inset-0 bg-[#121220]" />

      {/* Floating decorations */}
      <motion.div
        className="absolute top-10 left-10 z-20 hidden lg:block"
        animate={{ y: [0, -15, 0], rotate: [0, 360] }}
        transition={{
          y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
          rotate: { duration: 8, repeat: Infinity, ease: "linear" },
        }}
      >
        <img src={coinImg} alt="" className="w-14 h-14 drop-shadow-lg" />
      </motion.div>

      <motion.div
        className="absolute bottom-16 right-12 z-20 hidden lg:block"
        animate={{ y: [0, -10, 0], rotate: [-5, 5, -5] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <img src={moneyImg} alt="" className="w-20 h-auto opacity-60 drop-shadow-lg" />
      </motion.div>

      {/* Logo */}
      <motion.div
        className="mb-5 relative z-10"
        initial={{ opacity: 0, y: -30, scale: 0.8 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, type: "spring", bounce: 0.4 }}
      >
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="cursor-pointer rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F37020] focus-visible:ring-offset-2 focus-visible:ring-offset-[#121220]"
          aria-label="Go to dashboard"
        >
          <img src={ovalMoneyClubLogo} alt="Oval Money Club" className="h-24 sm:h-28 drop-shadow-lg" />
        </button>
      </motion.div>

      {/* Main Card */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
        className="w-full max-w-md relative z-10"
      >
        {/* Mascot peeking from top-right */}
        <motion.div
          className="absolute -top-12 -right-4 z-20 hidden sm:block"
          initial={{ opacity: 0, x: 40, rotate: 15 }}
          animate={{ opacity: 1, x: 0, rotate: 0 }}
          transition={{ delay: 0.6, type: "spring" }}
        >
          <motion.img
            src={ovalPillImg}
            alt=""
            className="w-20 h-20 drop-shadow-lg"
            animate={{ rotate: [-3, 3, -3], y: [0, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>

        <div className="bg-[#1C1C32] border border-[#2A2A45] rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-[#222240] px-6 py-5 relative overflow-hidden">
            {/* Animated stripes */}
            <motion.div
              className="absolute inset-0 opacity-15"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, transparent, transparent 10px, white 10px, white 20px)",
              }}
              animate={{ x: [0, 40] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
            <div className="relative text-center">
              <h1
                className="text-3xl sm:text-4xl font-black text-white"
                style={{
                  fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
                  letterSpacing: "0.04em",
                  textShadow: "3px 3px 0 rgba(0,0,0,0.3)",
                }}
              >
                Welcome to the Club!
              </h1>
              <p
                className="text-white text-sm mt-1 font-medium"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
              >
                Spin for your welcome bonus!
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-5 sm:p-6">
            <AnimatePresence mode="wait">
              {winAmount === null ? (
                <motion.div
                  key="slot"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <BankNoteSlotMachine onWin={handleWin} onSpinServer={handleServerSpin} prizes={prizes} />
                </motion.div>
              ) : (
                <motion.div
                  key="win"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <WinCelebration amount={winAmount} onContinue={handleContinue} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Footer tagline */}
      <motion.div
        className="mt-5 relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <motion.div
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C1C32] border border-[#2A2A45] rounded-full shadow-sm font-bold"
          animate={{ rotate: [-1, 1, -1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <Star className="h-4 w-4 text-[#F37020] fill-[#F37020]" />
          <span
            className="text-sm text-[#9A9ABF] flex items-center gap-1.5"
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            Powered by
            <img src={ovalLogo} alt="Oval" className="h-4 inline-block" />
          </span>
          <Star className="h-4 w-4 text-[#F37020] fill-[#F37020]" />
        </motion.div>
      </motion.div>
    </div>
  )
}
